import { useHistory } from 'react-router';
import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  Form,
  FormContainer,
  FormSectionContainer,
  ButtonGroupContainer,
  SectionContainer,
  LoadingSpinner,
  Line,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import {
  activateCardConstants,
  phoneMaxLength,
} from '../../../utils/constants/activate';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContextDispatch, useContextState } from '../../context/context';
import { ActivateCardInputs } from './sections';
import { useCallback, useRef, useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios, { AxiosRequestConfig } from 'axios';
import { globalConstants } from '../../../utils/constants/global';
import { OTPModal } from '../../modals';
const { activationCode, firstName, lastName, phoneNumber, termsConditionsCheck } =
  activateCardConstants.validations;
type ModalState = {phone: string | null, open: false, token: string | null} | {phone: string, token: string | null, open: true, resolve: (result: boolean) => void}


declare global {
  interface Window {
    grecaptcha: any;
  }
}

export const ActivateCard = () => {
  const dispatch = useContextDispatch();
  const { loading, otpToken } = useContextState();
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [isCaptchaCompleted, setCaptchaCompleted] = useState(false);
  const recaptcha = useRef<ReCAPTCHA>(null);

  const resetCaptcha = useCallback(() => {
    recaptcha.current?.reset();
    setCaptchaCompleted(false);
    setCaptchaToken("");
  }, []);

  const handleCaptchaChange = useCallback((value: string | null) => {
    setCaptchaCompleted(Boolean(value));
    setCaptchaToken(value || "");
  }, []);

  const [serverErrors, setServerErrors] = useState('');
  const [modalState, setModalState] = useState<ModalState>({phone: null, open: false, token: null})


  const { heading, subHeading, activateCardButtonText } =
    activateCardConstants;
  const { expiredCardMessage } = globalConstants;

  const { push } = useHistory();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    shouldFocusError: false,
    defaultValues: {
      activationCode: '',
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      termsConditionsCheck: false,
    },
  });
  const handleClose = () => {
    if (modalState.open) {
      modalState.resolve(true)
      setModalState({phone: '',open: false, token:null})
    }
  }

  const authenticate = async (phone: string) => {
    try{
      const data = {
        mobile_number: phone
      };
      await axios.post('/v1/requestotp', data, {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || ''
        }
      }
      );
      await new Promise((resolve) => {
        setModalState({open: true, token: null, phone: phone, resolve})
      } )
    }
    catch (err:any) {
      console.log('err', err);
      if (err.response.status === 400 || err.response.status === 401) {
        setServerErrors(err.response.data.message);
      }
    }
  };

  const authHeader = (token: string) => {
    // return auth header with jwt if a OTP was required for activation
    return { Authorization: `Bearer ${token}` };
  }

  const onSubmit = useCallback(async (formData: any) => {
    if (!isCaptchaCompleted) {
      setServerErrors('Captcha not verified');
      return;
    }
    try {
      dispatch('START_LOADING');
     setServerErrors('');
      const data = {
        fname: formData.firstName,
        lname: formData.lastName,
        eaid: formData.activationCode,
        phone: formData.phoneNumber,
        captchaToken: captchaToken,
      };
      let config: AxiosRequestConfig = {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || ''
        },
      };

      if (otpToken) {
        config = {
          ...config,
          headers: {
            ...config.headers,
            ...authHeader(otpToken),
          },
        };
      }

      const resp = await axios.post('/v1/activatecard', data, config);
      dispatch('STOP_LOADING');
      if (resp.status === 204){
        dispatch('START_LOADING');
        await authenticate(formData.phoneNumber)
        return;
      }
      dispatch('STOP_LOADING');
      if (resp.data.kyc_required === true) {
        push('/kyc-verification');
      } else {
        dispatch('CLEAR_OTP_TOKEN');
        push('/success');
      }
    } catch (err: any) {
      resetCaptcha()
      console.log('err', err);
      if (err.response.status === 700) {
        setServerErrors(expiredCardMessage);
      } else setServerErrors(err.response.data.message);
      dispatch('STOP_LOADING');
    }
  }, [captchaToken, dispatch, expiredCardMessage, isCaptchaCompleted, otpToken, push, resetCaptcha]);

  useEffect(() => {
    if (otpToken) {
      console.log('otpToken', otpToken)
      methods.handleSubmit(onSubmit)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpToken]);

  return (
    <>
      <Line />
      { modalState.open && <OTPModal {...modalState} handleClose={handleClose} /> }

      <FormProvider {...(methods as any)}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <SectionContainer>
            <Markdown children={heading} align="center" />
            <Markdown children={subHeading} align="center" />
            <FormContainer>
              <FormSectionContainer className="activateCardSection centered">
                <ActivateCardInputs />
                {serverErrors && (
                  <Markdown
                    children={serverErrors}
                    className="validationText"
                  />
                )}
                <ReCAPTCHA
                  ref={recaptcha}
                  sitekey="6LfU8UspAAAAAGYvuYdOkXrHcGGpuEXM4-hxH7Rh"
                  onChange={handleCaptchaChange}
                />
                <ButtonGroupContainer>
                  <Button
                    type="submit"
                    onClick={() => {
                      methods.handleSubmit(onSubmit);
                    }}
                  >
                    {loading ? <LoadingSpinner show /> : activateCardButtonText}
                  </Button>
                </ButtonGroupContainer>
              </FormSectionContainer>
            </FormContainer>
          </SectionContainer>
        </Form>
      </FormProvider>
    </>
  );
};
const validationSchema = Yup.object().shape({
  activationCode: Yup.string()
    .required(activationCode.required).max(10)
    .matches(/^[a-zA-Z0-9_.-]*$/, {
      message: activationCode.format,
      excludeEmptyString: true,
    }),
  firstName: Yup.string()
    .required(firstName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: firstName.format,
      excludeEmptyString: true,
    }),
  lastName: Yup.string()
    .required(lastName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: lastName.format,
      excludeEmptyString: true,
    }),
  phoneNumber: Yup.string()
    .required(phoneNumber.required)
    .matches(/^(0)[0-9]+$/, {
      message: phoneNumber.format,
      excludeEmptyString: true,
    })
    .min(phoneMaxLength, phoneNumber.minLength)
    .max(phoneMaxLength, phoneNumber.maxLength),
  termsConditionsCheck: Yup.bool().oneOf([true], termsConditionsCheck.required),
});
